import {
	ChatTimelineItem,
	ChatGroupPositionType,
	UserTimelineType,
	ChatTimelineMessage,
	ChatTimelineTimestamp,
	ChatEmailForm,
	ChatTimelineReply,
	ChatTimelineEvent
} from '../chat-timeline/types';
import * as moment from 'moment-timezone';

export type LocalizedTimeStrings = {
	today: string;
	yesterday: string;
};

export const getTimestamp = (time: number, locale: string, localizedTimeStrings: LocalizedTimeStrings): string => {
	moment.updateLocale(locale, {
		calendar: {
			lastDay: `[${localizedTimeStrings.yesterday}]`,
			sameDay: `[${localizedTimeStrings.today}]`,
			nextDay: '[Tomorrow]',
			lastWeek: 'dddd, MMMM Do',
			nextWeek: 'dddd, MMMM Do',
			sameElse: 'dddd, MMMM Do'
		}
	});
	return moment(time).calendar();
};

const timestampItem = (time: string): ChatTimelineTimestamp => {
	return {
		time,
		userTimelineType: UserTimelineType.TIMESTAMP
	};
};

export const chatMessagesTimestamper = (
		messages: Array<ChatEmailForm | ChatTimelineMessage | ChatTimelineEvent>,
		locale: string,
		localizedTimeString: LocalizedTimeStrings
	): ChatTimelineItem[] => {
		let stampedMessages: ChatTimelineItem[] = [];
		messages.forEach((message, idx) => {
			const time = getTimestamp(message.creationDate, locale, localizedTimeString);
			const maybeStamp = idx === 0 || messages[idx - 1] &&
				getTimestamp(messages[idx - 1].creationDate, locale, localizedTimeString) !== time;
			const item = maybeStamp
				? [timestampItem(time), message]
				: [message];
			stampedMessages = [
				...stampedMessages,
				...item
			];
		});
		return stampedMessages;
	};

export const groupChatMessages =
	(
		messages: ChatTimelineReply[],
		groupTime?: number
	): Array<ChatEmailForm | ChatTimelineMessage | ChatTimelineEvent> => {
		const MAX_TIME_GROUP = groupTime || 60000;
		let parsedMessages: Array<ChatEmailForm | ChatTimelineMessage | ChatTimelineEvent> = [];
		messages.forEach((message, idx) => {
			const isNextMessageUnderMaxTime =
				messages[idx + 1] && messages[idx + 1].creationDate - message.creationDate < MAX_TIME_GROUP;
			const lastParsedMessage = parsedMessages[idx - 1];
			if (isMessageEvent(message)) {
				parsedMessages = [
					...parsedMessages,
					message
				];
				// Checks for First or Only
			} else if (idx === 0 ||
				isMessageEvent(lastParsedMessage) ||
				message.creationDate - messages[idx - 1].creationDate > MAX_TIME_GROUP ||
				lastParsedMessage && lastParsedMessage.groupPosition === ChatGroupPositionType.LAST ||
				lastParsedMessage.groupPosition === ChatGroupPositionType.ONLY
			) {
				const maybeFirst = messages[idx + 1] && messages[idx + 1].user.id === message.user.id && isNextMessageUnderMaxTime;
				parsedMessages = [
					...parsedMessages,
					{
						...message,
						groupPosition: maybeFirst ? ChatGroupPositionType.FIRST : ChatGroupPositionType.ONLY
					}
				];
				// Checks for Middle or Last
			} else {
				const maybeMiddle = isNextMessageUnderMaxTime && messages[idx + 1].user.id === message.user.id;
				parsedMessages = [
					...parsedMessages,
					{
						...message,
						groupPosition: maybeMiddle ? ChatGroupPositionType.MIDDLE : ChatGroupPositionType.LAST
					}
				];
			}
		});
		return parsedMessages;
	};

export const getProcessedMessages = (
	rawMessages: ChatTimelineReply[], locale: string, localizedTimeStrings: LocalizedTimeStrings) =>
	chatMessagesTimestamper(groupChatMessages(rawMessages), locale, localizedTimeStrings);

const isMessageEvent = (message: ChatTimelineReply): message is ChatTimelineEvent =>
	message.userTimelineType === UserTimelineType.EVENT;
